import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Share2, AlertTriangle, ExternalLink, Clock, Eye } from 'lucide-react';
import axios from 'axios';
import PropertyResults from '../components/PropertyResults';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export default function SharedAnalysisPage() {
    const { shareId } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [shareData, setShareData] = useState(null);

    useEffect(() => {
        const fetchSharedAnalysis = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${API_URL}/api/share/${shareId}`);

                if (response.data.success) {
                    setShareData(response.data.data);
                } else {
                    setError(response.data.error || 'Failed to load shared analysis');
                }
            } catch (err) {
                console.error('Error fetching shared analysis:', err);
                if (err.response?.status === 404) {
                    setError('This shared link does not exist or has expired.');
                } else if (err.response?.status === 410) {
                    setError('This shared link has expired.');
                } else {
                    setError('Failed to load shared analysis. Please try again.');
                }
            } finally {
                setLoading(false);
            }
        };

        if (shareId) {
            fetchSharedAnalysis();
        }
    }, [shareId]);

    // Loading State
    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-gray-50 flex items-center justify-center p-4">
                <div className="text-center">
                    <div className="relative w-16 h-16 mx-auto mb-4">
                        <div className="absolute inset-0 border-4 border-emerald-200 rounded-full"></div>
                        <div className="absolute inset-0 border-4 border-t-emerald-600 rounded-full animate-spin"></div>
                    </div>
                    <p className="text-gray-600 font-medium">Loading shared analysis...</p>
                </div>
            </div>
        );
    }

    // Error State
    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 to-gray-50 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border-2 border-red-200 p-8 text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AlertTriangle className="w-8 h-8 text-red-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Unable to Load</h2>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <a
                        href="/"
                        className="inline-block px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
                    >
                        Go to ProprScout Home
                    </a>
                </div>
            </div>
        );
    }

    // Success State - Display Analysis
    return (
        <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-gray-50">
            {/* Header with ProprScout Branding */}
            <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
                                <span className="text-white font-bold text-lg">P</span>
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-gray-900">ProprScout</h1>
                                <p className="text-xs text-gray-500">Shared Property Analysis</p>
                            </div>
                        </div>

                        <a
                            href="/"
                            className="px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2"
                        >
                            <ExternalLink className="w-4 h-4" />
                            Analyze Your Property
                        </a>
                    </div>
                </div>
            </header>

            {/* Shared Analysis Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Share Info Banner */}
                <div className="bg-emerald-100 border-2 border-emerald-300 rounded-xl p-4 mb-6 flex items-start gap-3">
                    <Share2 className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm font-semibold text-emerald-900">
                            🔗 This is a shared property analysis
                        </p>
                        <div className="flex flex-wrap gap-4 mt-2 text-xs text-emerald-700">
                            {shareData.views && (
                                <div className="flex items-center gap-1">
                                    <Eye className="w-3 h-3" />
                                    <span>{shareData.views} views</span>
                                </div>
                            )}
                            {shareData.expiresAt && shareData.expiresAt !== 'never' && (
                                <div className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    <span>
                                        Expires {new Date(shareData.expiresAt).toLocaleDateString()}
                                    </span>
                                </div>
                            )}
                            <div className="flex items-center gap-1">
                                <span>Shared {new Date(shareData.createdAt).toLocaleDateString()}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Render the Property Analysis */}
                {shareData.analysis && shareData.propertyData && (
                    <PropertyResults
                        result={{
                            propertyData: shareData.propertyData,
                            analysis: shareData.analysis,
                            geolocation: shareData.analysis.geolocation
                        }}
                        loading={false}
                        error={null}
                    />
                )}

                {/* CTA Footer */}
                <div className="mt-12 bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-2xl p-8 text-center text-white shadow-xl">
                    <h3 className="text-2xl font-bold mb-2">Like What You See?</h3>
                    <p className="text-emerald-100 mb-6 max-w-2xl mx-auto">
                        Get AI-powered property analysis for any Portuguese listing. Instant insights, transparent scoring, and professional reports.
                    </p>
                    <div className="flex flex-wrap gap-4 justify-center">
                        <a
                            href="/signup"
                            className="px-8 py-4 bg-white text-emerald-600 font-bold rounded-lg hover:bg-emerald-50 transition-colors shadow-lg"
                        >
                            Sign Up Free
                        </a>
                        <a
                            href="/dashboard/listing-analysis"
                            className="px-8 py-4 bg-emerald-500 text-white font-bold rounded-lg hover:bg-emerald-400 transition-colors border-2 border-white"
                        >
                            Try ProprScout
                        </a>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-white border-t border-gray-200 mt-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <p className="text-center text-sm text-gray-500">
                        © 2026 ProprScout - AI-Powered Portuguese Property Analysis
                    </p>
                </div>
            </footer>
        </div>
    );
}
